import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import ExpandableGrid from '@prisjakt/ui-expandable-grid';
import ProductImage from '@prisjakt/ui-product-image';
import ProductCard from '@prisjakt/ui-product-card';

import MatchMedia from '../match-media';

import { productNavigation } from '../../areas/product/actions/product';
import { RECENTLY_VISITED_CLICK } from '../../globals/helpers/navigation-action';

import { readRecentlyVisited } from './storage';
import DataContainer from '../data';
import recentlyVisitedProductsQuery from './query.graphql';

const MAX_PRODUCTS_IN_GRID = 12;

const resolveProducts = (productIds, productData) => {
  // For various reasons, some products may be missing from the data array,
  // e.g. if they've been removed since the user visited the product. We must
  // filter out those products and simply show as many recent productcs as
  // possible.
  const productById = productData.reduce(
    (map, product) => ({
      ...map,
      [product.id]: product,
    }),
    {}
  );

  return productIds.map(id => productById[id]).filter(Boolean);
};

const RecentlyVisitedProducts = props => {
  const { t } = useContext(AppContext);
  const { tracking, offset } = props;

  if (!tracking.settings.personalization) {
    // The user has disabled personalization altogether!
    return null;
  }

  const recentlyVisited = {
    products: readRecentlyVisited().products.slice(offset),
  };
  const productIds = recentlyVisited.products.map(p => p.id).filter(Boolean);

  if (
    !recentlyVisited ||
    !recentlyVisited.products ||
    recentlyVisited.products.length === 0
  ) {
    return null;
  }

  return (
    <DataContainer
      getKey={() => 'recentlyVisitedProducts'}
      variables={{ recentlyVisitedProductIds: productIds }}
      query={RecentlyVisitedProducts.graphQL}
      render={({ data }) => {
        if (!data) {
          return null;
        }

        const resolvedVisitedProducts = resolveProducts(
          productIds,
          data.recentlyVisitedProducts
        ).slice(0, MAX_PRODUCTS_IN_GRID);

        if (resolvedVisitedProducts.length < 4) {
          return null;
        }

        return (
          <Panel
            header={
              <PanelHeader
                title={t('landing', 'recently-visited-products.title')}
              />
            }
          >
            <MatchMedia
              debounce={1000}
              serverSideRender={{ tablet: false }}
              matches={[
                {
                  mediaQuery: '(min-width: 640px) and (max-width: 1000px)',
                  values: { tablet: true },
                },
                {
                  mediaQuery: 'all',
                  values: { tablet: false },
                },
              ]}
            >
              {({ tablet }) => (
                <ExpandableGrid
                  limit={tablet ? 3 : 4}
                  expandText={t('common', 'button.show-more')}
                  collapseText={t('common', 'button.show-less')}
                  items={resolvedVisitedProducts.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      link={product.pathName}
                      name={product.name}
                      image={
                        <ProductImage
                          name={product.name}
                          src={product.media.first}
                          fallback={product.category && product.category.logo}
                        />
                      }
                      price={
                        product.priceSummary.regular ||
                        product.priceSummary.alternative
                      }
                      rating={product.userReviewSummary.rating}
                      onClick={() => {
                        props.productNavigation(
                          RECENTLY_VISITED_CLICK,
                          index + 1
                        );
                      }}
                    />
                  ))}
                />
              )}
            </MatchMedia>
          </Panel>
        );
      }}
    />
  );
};

const { number, string, bool, arrayOf, shape } = PropTypes;

RecentlyVisitedProducts.propTypes = {
  offset: number,
  products: arrayOf(
    shape({
      id: number,
      name: string,
      priceSummary: shape({
        regular: number,
        alternative: number,
        rating: number,
      }),
      userReviewSummary: shape({
        count: number,
        rating: number,
      }),
      pathName: string,
      media: shape({
        first: string,
      }),
      category: shape({
        logo: string,
      }),
    }).isRequired
  ),
  tracking: shape({
    settings: shape({
      personalization: bool,
    }),
  }).isRequired,
  productNavigation: PropTypes.func,
};

RecentlyVisitedProducts.defaultProps = {
  offset: 0,
  products: null,
  productNavigation: () => {},
};

RecentlyVisitedProducts.graphQL = recentlyVisitedProductsQuery;

export default connect(
  state => ({ tracking: state.areas.user.tracking }),
  { productNavigation }
)(memo(RecentlyVisitedProducts));
